import { UserBranch } from "@/Pages/Auth/Klanten/Users/UserBranches";
import { DeliveryMethods } from "@/shared/utils/enum";
import { getStatusBy } from "@/shared/utils/helpers";
import dayjs from "dayjs";
import { CartContent, OrderUser, OrderV2 } from "../queries/interfacesV2";

export const getOrderUserName = (user: OrderUser) =>
  user?.name ?? `${user?.first_name ?? "-"} ${user?.last_name ?? ""}`;

export const getDeliveryOrders = (orders: OrderV2[]) =>
  orders.filter(
    (order) =>
      order.order_information.delivery_method_id === DeliveryMethods.DELIVERY,
  );

export const orderWithGeo = (order: OrderV2) =>
  Number.isFinite(parseFloat(order?.order_user?.address_geo_coordinates?.[3])) &&
  Number.isFinite(parseFloat(order?.order_user?.address_geo_coordinates?.[2]));

export const getGeoOrder = (order: OrderV2) => {
  const hasGeo = orderWithGeo(order);
  if (!hasGeo) {
    return {
      lng: undefined,
      lat: undefined,
    };
  }
  return {
    lng: parseFloat(order?.order_user?.address_geo_coordinates?.[3]),
    lat: parseFloat(order?.order_user?.address_geo_coordinates?.[2]),
  };
};

export const getUniqueOrderColor = (order: OrderV2 | "0") => {
  function numberToColorRGBA(num) {
    // Generate a pseudo-random but unique number based on input
    let hue = (num * 137.508) % 360; // Use a prime-based multiplier for uniform distribution

    // Exclude red and red shades (hue 0-30 and 330-360)
    if (hue < 30) {
      hue += 30; // Shift to 30-60
    } else if (hue > 330) {
      hue -= 30; // Shift to 300-330
    }

    const saturation = 70; // Set saturation to 70% for vibrant colors
    const lightness = 50; // Set lightness to 50% for balanced colors

    // Convert HSL to RGB
    const rgb = hslToRgb(hue, saturation / 100, lightness / 100);

    // Add alpha (opacity) value, set to 1 for full opacity
    const rgba = [...rgb, 255];
    return rgba;
  }

  // Function to convert HSL to RGB
  function hslToRgb(h, s, l) {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;

    let r = 0,
      g = 0,
      b = 0;

    if (0 <= h && h < 60) {
      r = c;
      g = x;
      b = 0;
    } else if (60 <= h && h < 120) {
      r = x;
      g = c;
      b = 0;
    } else if (120 <= h && h < 180) {
      r = 0;
      g = c;
      b = x;
    } else if (180 <= h && h < 240) {
      r = 0;
      g = x;
      b = c;
    } else if (240 <= h && h < 300) {
      r = x;
      g = 0;
      b = c;
    } else if (300 <= h && h < 360) {
      r = c;
      g = 0;
      b = x;
    }

    // Normalize to [0, 255]
    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);

    return [r, g, b];
  }

  return numberToColorRGBA(
    order === "0" ? Math.random() : order?.order_information?.item_id,
  );
};

export const isOrderPaid = (paymentMethod: number) =>
  paymentMethod % 2 === 0 || paymentMethod === 13 || paymentMethod === 9;

export function groupBy(data, key) {
  return data.reduce((result, current) => {
    // Get the value of the specified key
    const keyValue = current?.[key];
    // Initialize the group if it doesn't exist
    if (!result[keyValue]) {
      result[keyValue] = [];
    }
    // Add the current object to the group
    result[keyValue].push(current);
    return result;
  }, {});
}
export const getOrderOrderStatusHistoryDiff = (
  obj,
): {
  key: string;
  hour?: string;
  min?: string;
  old: any;
  new: any;
}[] => {
  const validOrder = [1, 2, 3, 7, 4, 5].filter((key) =>
    obj.hasOwnProperty(key),
  );

  let response = [];
  // Get the keys sorted in ascending numerical order

  // Check if the object is empty or has only one property
  if (validOrder.length <= 1) {
    return response;
  }

  // Loop over the keys and calculate time differences
  for (let i = 0; i < validOrder.length - 1; i++) {
    const currentKey = validOrder[i];
    const nextKey = validOrder[i + 1];

    const currentTime = dayjs(obj[currentKey]);
    const nextTime = dayjs(obj[nextKey]);

    // Calculate the difference in minutes
    const differenceInMinutes = nextTime.diff(currentTime, "minute");

    if (differenceInMinutes >= 60) {
      // If the difference is more than or equal to an hour
      const formattedDifference = dayjs
        .duration(differenceInMinutes, "minute")
        .format("HH:mm");
      response = [
        ...response,
        {
          key: "betweenMethodsInHour",
          hour: formattedDifference,
          old: getStatusBy(currentKey),
          new: getStatusBy(nextKey),
        },
      ];
    } else {
      response = [
        ...response,
        {
          key: "betweenMethodsInMinutes",
          min: `${differenceInMinutes}`,
          old: getStatusBy(currentKey),
          new: getStatusBy(nextKey),
        },
      ];
    }
  }

  return response;
};

export const splitOrderByKitchenScreen = (order: OrderV2): OrderV2[] => {
  const split: {
    [key: number]: CartContent[];
  } = order.cart_content
    .filter(
      (item) =>
        (item.type === "1" || item.type === "9") &&
        item.kitchen_screen_id !== undefined,
    )
    .reduce((acc, item) => {
      if (item.kitchen_screen_id in acc) {
        acc[item.kitchen_screen_id].push(item);
      } else {
        acc[item.kitchen_screen_id] = [item];
      }
      return acc;
    }, {});

  return Object.entries(split).map(([key, values]) => {
    return { ...order, cart_content: values };
  });
};

export const toAllowedBranchesType = (
  branches: number[],
  allowed_branch_id_expiration_date_time: {
    [key: number]: string;
  },
): UserBranch[] => {
  return branches.map((branch) => ({
    branch_id: branch,
    expiration_date: allowed_branch_id_expiration_date_time?.[branch] ?? "",
  }));
};

export const generateBranchNameLink = (name: string) =>
  `${name?.toLocaleLowerCase().replaceAll(/\s+/g, "-").replaceAll(".", "-")}/`;
